<script setup>
import {ref, nextTick, computed, onMounted} from 'vue'
import {storeToRefs} from 'pinia'
import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/swiper-bundle.css'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {useUserStore} from '@/stores/UserStore'
import {companiesToSeeSnapchatBanner} from '@/views/home-page/Variables'
import {loadSnoozedBannerKeys, snoozeBanner} from '@/components/ui/swiper/bannerSnoozeService'

const EXTERNAL_LINK_ATTRIBUTES = {target: '_blank', rel: 'nofollow noopener noreferrer'}

const {sendAnalytics} = useDefaultStore()
const {userManagerInfo} = storeToRefs(useUserStore())
const {user} = storeToRefs(useAuthenticationStore())

const isSnapchatBannerVisible = computed(
  () => companiesToSeeSnapchatBanner.includes(user?.value?.company?.hash) && userManagerInfo.value,
)

const banners = computed(() => [
  {
    key: 'pinterest_promo',
    image: '/images/banners/pinterest-banner.webp',
    alt: 'Pinterest',
    linkComponent: 'router-link',
    linkAttributes: {to: {name: 'pinterest-ad-account-request'}},
    isAvailable: true,
  },
  {
    key: 'creative_studio_promo',
    image: '/images/banners/creative-studio-banner.webp',
    alt: 'Creative Studio',
    linkComponent: 'router-link',
    linkAttributes: {to: {name: 'creative-studio-main'}},
    isAvailable: true,
  },
  {
    key: 'mintegral_mediago_promo',
    image: '/images/banners/mintegral-banner.webp',
    alt: 'Mintegral and MediaGo',
    linkComponent: 'a',
    linkAttributes: {
      href: 'https://webforms.pipedrive.com/f/1wVwxZMbL2zX1hV217o3eJxd39YQeVp23mg2NSwRyBssUKfCiDEoD9aDkuaHOhmuv',
      ...EXTERNAL_LINK_ATTRIBUTES,
    },
    isAvailable: true,
  },
  {
    key: 'snapchat_smm_2025',
    image: '/images/banners/snapchat-banner-smm.webp',
    alt: 'Snapchat Event',
    linkComponent: 'a',
    linkAttributes: {
      href: userManagerInfo.value?.[0]?.telegram || `mailto:${userManagerInfo.value?.[0]?.email}`,
      ...EXTERNAL_LINK_ATTRIBUTES,
    },
    isAvailable: Boolean(isSnapchatBannerVisible.value),
  },
  {
    key: 'standard_promo',
    image: '/images/banners/main-banner.webp',
    alt: 'PointBoard',
    linkComponent: 'a',
    linkAttributes: {
      href: 'https://webforms.pipedrive.com/f/1HHzSHdA9BDUgcWUSjEdyqSNbbFBaJT47ewjOzDYJ5eh67WVhcTAvutUIxmBJdGJt',
      ...EXTERNAL_LINK_ATTRIBUTES,
    },
    isAvailable: true,
  },
])

const snoozedBannerKeys = ref([])
const areSnoozesLoaded = ref(false)
const activeBannerIndex = ref(0)

const visibleBanners = computed(() =>
  banners.value.filter(banner => banner.isAvailable && !snoozedBannerKeys.value.includes(banner.key)),
)
const visibleBannersKey = computed(() => visibleBanners.value.map(banner => banner.key).join('|'))
const activeBanner = computed(() => visibleBanners.value[activeBannerIndex.value] ?? null)

const prevButton = ref(null)
const nextButton = ref(null)
const pagination = ref(null)
const swiperInstance = ref(null)

onMounted(async () => {
  snoozedBannerKeys.value = await loadSnoozedBannerKeys()
  areSnoozesLoaded.value = true
})

const onSwiperInit = swiper => {
  swiperInstance.value = swiper
  activeBannerIndex.value = 0

  nextTick(() => {
    swiper.params.navigation.prevEl = prevButton.value
    swiper.params.navigation.nextEl = nextButton.value
    swiper.params.pagination.el = pagination.value

    swiper.navigation.init()
    swiper.pagination.init()
  })
}

const onSlideChange = swiper => {
  activeBannerIndex.value = swiper.realIndex
}

const onShowLaterClick = () => {
  if (!activeBanner.value) {
    return
  }

  const bannerKey = activeBanner.value.key

  snoozedBannerKeys.value = [...snoozedBannerKeys.value, bannerKey]
  snoozeBanner(bannerKey)
}

const pauseAutoplay = () => {
  swiperInstance.value?.autoplay.stop()
}

const resumeAutoplay = () => {
  swiperInstance.value?.autoplay.start()
}

const onBannerClick = banner => {
  handleSendAnalytics({event: banner.key})
}

const handleSendAnalytics = async params => {
  try {
    await sendAnalytics(params)
  } catch (error) {
    console.log(error)
  }
}
</script>

<template>
  <div v-if="areSnoozesLoaded && visibleBanners.length" class="swiper-container">
    <div class="swiper-inner" @mouseenter="pauseAutoplay" @mouseleave="resumeAutoplay">
      <button
        type="button"
        class="show-later-button btn btn-secondary position-absolute top-0 translate-middle-y z-2 d-flex align-items-center rounded-pill custom gap-1 py-1 pl-3 fs-4"
        @click="onShowLaterClick"
      >
        Show later
        <svg
          class="show-later-button__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M12.4661 13.1724C12.6598 13.3661 12.9786 13.3661 13.1723 13.1724C13.3661 12.9786 13.3661 12.6599 13.1723 12.4661L9.03169 8.3255L13.1723 4.18488C13.3661 3.99113 13.3661 3.67238 13.1723 3.47863C12.9786 3.28488 12.6598 3.28488 12.4661 3.47863L8.32544 7.61925L4.18794 3.47863C3.99419 3.28488 3.67544 3.28488 3.48169 3.47863C3.28794 3.67238 3.28794 3.99113 3.48169 4.18488L7.61919 8.3255L3.47856 12.4661C3.28481 12.6599 3.28481 12.9786 3.47856 13.1724C3.67231 13.3661 3.99106 13.3661 4.18481 13.1724L8.32544 9.03175L12.4661 13.1724Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <swiper
        :key="visibleBannersKey"
        :slides-per-view="1"
        :space-between="10"
        :navigation="{prevEl: prevButton, nextEl: nextButton}"
        :autoplay="{delay: 6000}"
        :loop="visibleBanners.length > 1"
        class="my-swiper"
        @swiper="onSwiperInit"
        @slide-change="onSlideChange"
      >
        <swiper-slide v-for="banner in visibleBanners" :key="banner.key" @click="onBannerClick(banner)">
          <div class="slide-content">
            <component :is="banner.linkComponent" v-bind="banner.linkAttributes">
              <img :src="banner.image" :alt="banner.alt" class="d-block w-100 max-w-100 border-radius-lg" />
            </component>
          </div>
        </swiper-slide>
      </swiper>
      <div class="custom-navigation">
        <button ref="prevButton" class="custom_swiper-button custom_swiper-button-prev">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.6666 10.6667L13.3333 16L18.6666 21.3333"
              stroke="black"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button ref="nextButton" class="custom_swiper-button custom_swiper-button-next fa-rotate-180">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.6666 10.6667L13.3333 16L18.6666 21.3333"
              stroke="black"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
    <div ref="pagination" class="custom-pagination"></div>
  </div>
</template>

<style lang="scss">
.swiper-container {
  position: relative;
}

.swiper-inner {
  position: relative;

  &:hover .custom-navigation,
  &:hover .show-later-button {
    opacity: 1;
  }
}

.custom_swiper-button {
  cursor: pointer !important;
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 0;
  width: 38px;
  height: 38px;
  border: 2px solid #d9d9d9;
  border-radius: 50%;
  background-color: #fff;
  padding: 0;
  position: absolute;
  top: calc(50% - 19px);
  z-index: 1;

  transition: all ease-in-out 0.1s;

  & svg path {
    transition: all ease-in-out 0.1s;
  }

  &:hover {
    background-color: #fceae4;
    border-color: #fb6639;
  }

  &:hover svg path {
    stroke: #fb6639;
  }
}

.custom_swiper-button-prev {
  left: 10px;
}

.custom_swiper-button-next {
  right: 10px;
}

.custom-navigation {
  opacity: 0;
}

.show-later-button {
  opacity: 0;
  right: toRem(-12);
  padding-right: toRem(6) !important;
  color: $text-secondary !important;
  line-height: 1.3;
  transition: opacity ease-in-out 0.1s;

  &:focus-visible {
    opacity: 1;
  }
}

.show-later-button__icon {
  flex-shrink: 0;
  color: $text-muted;
}

.custom-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 12px;
}

.swiper-pagination-bullet {
  width: 6px;
  height: 6px;
  background-color: #030712;
}
</style>
